
import { Link } from "react-router-dom";
import { 
  Table, 
  TableBody,
  TableCell,
  TableRow
} from "@/components/ui/table";
import { MessageSquare, Eye, ThumbsUp, Calendar } from "lucide-react";
import { Post } from "@/types/board";
import { useIsMobile } from "@/hooks/use-mobile";

interface PostsTableProps {
  posts: Post[];
  isLoading: boolean;
  formatDate: (date: string) => string;
  getAuthorName: (post: Post) => string;
  handlePostClick: (post: Post, e: React.MouseEvent) => void;
}

const PostsTable = ({ 
  posts, 
  isLoading, 
  formatDate, 
  getAuthorName,
  handlePostClick 
}: PostsTableProps) => {
  const isWeeLinkCategory = posts.length > 0 && posts.every(post => post.category === 'Weee Link 공유');
  const isMobile = useIsMobile();

  // 모바일에서 클릭 시 스크롤 최상단 이동을 감싸는 함수
  const onPostClick = (post: Post, e: React.MouseEvent) => {
    if (isMobile) {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }
    handlePostClick(post, e);
  };

  return (
    <Table>
      {/* TableHeader 제거 */}
      <TableBody>
        {isLoading ? (
          <TableRow>
            <TableCell colSpan={isWeeLinkCategory || isMobile ? 3 : 5} className="text-center py-8">
              게시글을 불러오는 중...
            </TableCell>
          </TableRow>
        ) : posts.length > 0 ? (
          posts.map((post) => {
            const commentCount = post.commentCount || post.comment_count || 0;

            if (isMobile) {
              // 모바일: 제목 강조, 작성자/작성일/조회수/댓글수를 분리하여 디자인
              return (
                <TableRow key={post.id} className="align-top">
                  <TableCell colSpan={5} className="!p-4">
                    <Link 
                      to={`/board/post/${post.id}`} 
                      className="block"
                      onClick={(e) => onPostClick(post, e)}
                    >
                      {/* 제목 크게 (더 강조) */}
                      <div className="font-bold text-lg mb-2 leading-snug text-ellipsis line-clamp-2">
                        {post.title}
                      </div>
                      {/* 작성자 (간단명료), 바로 아래에 글쓴시간, 조회수, 댓글수 (간결하게) */}
                      <div className="text-sm text-muted-foreground flex items-center gap-2 mb-1">
                        <span>{getAuthorName(post)}</span>
                      </div>
                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        <span>{formatDate(post.createdAt || post.created_at)}</span>
                        {!isWeeLinkCategory && (
                          <>
                            <span className="flex items-center">
                              <Eye className="h-3 w-3 mr-1" />
                              {post.viewCount || post.view_count || 0}
                            </span>
                            <span className="flex items-center">
                              <MessageSquare className="h-3 w-3 mr-1" />
                              {commentCount}
                            </span>
                          </>
                        )}
                      </div>
                    </Link>
                  </TableCell>
                </TableRow>
              );
            }

            // 데스크톱: 헤더 없이 깔끔하게
            return (
              <TableRow key={post.id}>
                <TableCell>
                  <Link 
                    to={`/board/post/${post.id}`} 
                    className="hover:text-primary hover:underline"
                    onClick={(e) => handlePostClick(post, e)}
                  >
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-base leading-snug">{post.title}</span>
                      {commentCount > 0 && (
                        <span className="text-muted-foreground text-sm flex items-center">
                          <MessageSquare className="h-3 w-3 mr-1" />
                          {commentCount}
                        </span>
                      )}
                    </div>
                  </Link>
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">{getAuthorName(post)}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{formatDate(post.createdAt || post.created_at)}</TableCell>
                {!isWeeLinkCategory && (
                  <>
                    <TableCell className="text-center">{post.viewCount || post.view_count || 0}</TableCell>
                    <TableCell className="text-center">{post.likeCount || post.like_count || 0}</TableCell>
                  </>
                )}
              </TableRow>
            );
          })
        ) : (
          <TableRow>
            <TableCell colSpan={isWeeLinkCategory || isMobile ? 3 : 5} className="text-center py-8 text-muted-foreground">
              게시글이 없습니다.
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
};

export default PostsTable;
